"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Net = exports.NetTypeSchema = exports.PinMap = exports.Pin = void 0;
const shared_1 = require("@buildwithflux/shared");
const zod_1 = require("zod");
const element_1 = require("./element");
const constants_1 = require("./part/constants");
const uid_1 = require("./uid");
/**
 * A pin is a specific terminal on a specific element.
 */
exports.Pin = zod_1.z.object({
    uid: zod_1.z.string(),
    elementUid: zod_1.z.string(),
    terminalUid: zod_1.z.string(),
    // Order is important for determining which pin wins for impedance matching
    order: zod_1.z.number().optional(),
});
/**
 * Just a simple container type for fast lookups on nets
 */
exports.PinMap = zod_1.z.record(exports.Pin.shape.uid, exports.Pin);
const netTypes = ["Power", "High Speed", "Medium Speed", "Low Speed", "Analog", "Ground", "Uncertain"];
exports.NetTypeSchema = zod_1.z.enum(netTypes);
class Net {
    nodeNumber;
    // Just the important (non-branchpoint) terminals:
    terminals = new Set();
    connectedElements = new Set();
    internal = false;
    hash;
    constructor(terminals, options = {}) {
        this.nodeNumber = options.nodeNumber ?? -1;
        this.addTerminals(terminals);
    }
    static terminalsIncludeGround(topLevelNetTerminals, allElements) {
        return topLevelNetTerminals.some((t) => (0, element_1.isGroundTerminal)(allElements[(0, uid_1.splitMultipartUID)(t)[0]], t));
    }
    static terminalsToMap(terminals) {
        return Object.fromEntries([...terminals].map((t) => [t, true]));
    }
    isGroundNet() {
        // Only test top-level terminals, because submodule grounds aren't grounds in a host
        // document unless connected to host ground. Also, don't need to test element category
        // === ground here, because that's done in netMapping.ts when the net graph is created.
        return Net.terminalsIncludeGround(this.topLevelTerminals(), {}); // Ok to pass in empty `allElements` because grounded terminals have already been mapped to GROUND_TERMINAL_UID when Net was created in generateNets()
    }
    computeHash() {
        const terminalIds = ["", ...this.topLevelTerminals()];
        // In my tests, calculating the hash one time is about 2x faster than calculating
        // it incrementally (mainly because of the hexToBytes->bytesToHex round trip).
        // Ultimately, the values are still xor'ed sequentially together, but there's less
        // overhead.
        return (0, shared_1.orderIndependentHash)(terminalIds); // Safe to cast away `undefined`, because "" ensures at least one entry.
    }
    getHash() {
        if (this.hash === undefined) {
            this.hash = this.computeHash();
        }
        return this.hash;
    }
    terminalsByDocLevel() {
        const terminals = { topLevel: [], submodule: [] };
        for (const terminalId of this.terminals) {
            const [elementUid] = (0, uid_1.splitMultipartUID)(terminalId);
            const [ancestorUid] = (0, uid_1.splitMultipartUID)(elementUid);
            if (ancestorUid) {
                terminals.submodule.push(terminalId);
            }
            else {
                terminals.topLevel.push(terminalId);
            }
        }
        return terminals;
    }
    topLevelTerminals() {
        return this.terminalsByDocLevel().topLevel;
    }
    addTerminals(terminals) {
        for (const [terminalId, elementId] of Object.entries(terminals)) {
            this.connectedElements.add(elementId);
            const [, bareTerminalUid] = (0, uid_1.splitMultipartUID)(terminalId);
            // Ignore branchpoint terminals, because they don't contribute to the circuit or the net hash:
            if (bareTerminalUid === constants_1.ROUTE_BRANCH_POINT_TERMINAL_UID)
                continue;
            this.terminals.add(terminalId);
        }
    }
}
exports.Net = Net;
//# sourceMappingURL=net.js.map